<script setup lang="ts">
/**
 * Une carte du catalogue (`carteType` S:6207-6270), en lecture.
 *
 * Ce que l'utilisateur doit vivre : il lit, dans ses mots, ce que le type
 * d'alarme surveille, sur quel type d'équipement, s'il est à traiter ou pour
 * information, ce qui arrive si personne n'agit, quand il est prévenu, et ce
 * qu'il pourra régler. Tous les mots viennent des textes du type ; un texte non
 * servi se dit, il ne se remplace pas. Aucun bouton : créer une surveillance
 * vient au lot F8.
 */
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import type { AlarmesV2TypeAlarme } from "@modules/alarmes-v2/types/alarmes-v2"
import {
    equipementsDuType,
    estPourInformation,
    mesureDuType,
    nomDuTypeDuCatalogue,
} from "@modules/alarmes-v2/utils/catalogue"
import { formaterMesure } from "@modules/alarmes-v2/utils/mesure"
import { MESSAGES_ALARMES_V2 } from "@modules/alarmes-v2/i18n/fr"

/** Une clé propre de l'objet, jamais une clé héritée (`constructor`…). */
const possede = (o: object, cle: string): boolean =>
    Object.prototype.hasOwnProperty.call(o, cle)

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    type: AlarmesV2TypeAlarme
}>()

// ================================
// COMPUTED
// ================================
const getNom = computed(() => nomDuTypeDuCatalogue(props.type, tr))

const getInformation = computed(() => estPourInformation(props.type))

const getBadge = computed((): string => {
    if (getInformation.value) return t("alarmesV2.catalogue.carte.information")
    return props.type.astreinte === true
        ? t("alarmesV2.catalogue.carte.aTraiterAstreinte")
        : t("alarmesV2.catalogue.carte.aTraiter")
})

const getSousTitre = computed(() =>
    t("alarmesV2.catalogue.carte.sePoseSur", {
        mesure: mesureDuType(props.type, tr),
        equipements: equipementsDuType(props.type, tr),
    })
)

const texte = (v: unknown): string =>
    typeof v === "string" && v.trim() !== "" ? v : ""

const getPhrase = computed(() => texte(props.type.verbatims?.phrase))
const getConsequence = computed(() =>
    texte(props.type.verbatims?.si_personne_n_agit)
)
const getQuand = computed(() =>
    texte(props.type.verbatims?.quand_vous_etes_prevenu)
)

/** Les paramètres réglables, nommés ; `null` : la liste n'est pas servie. */
const getParametres = computed(
    (): { libelle: string; texte: string }[] | null => {
        const nombres = props.type.nombres
        if (!Array.isArray(nombres)) return null
        const reglables = nombres.filter((n) => n && n.reglable !== false)
        const nommes = reglables.filter((n) => texte(n.libelle))
        // Un réglable sans libellé ne se dit pas « rien à régler ».
        if (nommes.length < reglables.length) return null
        return nommes.map((n) => {
            const fm = (v: number | null | undefined) =>
                formaterMesure(v, n.unite, tr)
            const min = n.plage?.min
            const max = n.plage?.max
            let phrase: string
            if (typeof n.valeur_proposee !== "number")
                phrase = t("alarmesV2.catalogue.carte.reglable")
            else if (typeof min === "number" && typeof max === "number")
                phrase = t("alarmesV2.catalogue.carte.proposee", {
                    valeur: fm(n.valeur_proposee),
                    min: fm(min),
                    max: fm(max),
                })
            else
                phrase = t("alarmesV2.catalogue.carte.proposeeSansPlage", {
                    valeur: fm(n.valeur_proposee),
                })
            return { libelle: n.libelle as string, texte: phrase }
        })
    }
)

/** « Proposé par SMS et mail. », ou `""` si les canaux ne sont pas servis. */
const getCanaux = computed((): string => {
    const canaux = MESSAGES_ALARMES_V2.envois.canaux as Record<string, string>
    const servis = Array.isArray(props.type.canaux)
        ? props.type.canaux.filter((c) => possede(canaux, c))
        : []
    if (servis.length === 0) return ""
    return t("alarmesV2.catalogue.carte.canaux", {
        canaux: servis
            .map((c) => t(`alarmesV2.envois.canaux.${c}`))
            .join(t("alarmesV2.catalogue.et")),
    })
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <article
        class="flex flex-col gap-2 rounded-lg border border-surface-200 bg-surface-0 p-4"
        data-test="carte-type"
    >
        <header class="flex flex-wrap items-center gap-2">
            <h2 class="text-base font-semibold text-surface-900">
                {{ getNom }}
            </h2>
            <span
                class="rounded border px-2 py-0.5 text-xs font-medium"
                :class="
                    getInformation
                        ? 'border-sky-200 bg-sky-50 text-sky-800'
                        : 'border-surface-300 bg-surface-50 text-surface-800'
                "
                data-test="carte-nature"
                >{{ getBadge }}</span
            >
            <span class="basis-full text-xs text-surface-600">{{
                getSousTitre
            }}</span>
        </header>
        <p v-if="getPhrase" class="text-sm">{{ getPhrase }}</p>
        <p v-else class="text-sm text-surface-600" data-test="sans-phrase">
            {{ t("alarmesV2.catalogue.carte.sansPhrase") }}
        </p>
        <p v-if="getConsequence" class="text-sm text-surface-700">
            <strong>{{
                t("alarmesV2.catalogue.carte.siPersonneNAgit")
            }}</strong>
            {{ getConsequence }}
        </p>
        <details class="mt-1">
            <summary class="cursor-pointer text-sm font-medium">
                {{ t("alarmesV2.catalogue.carte.details") }}
            </summary>
            <div class="mt-2 flex flex-col gap-2 text-sm">
                <template v-if="getParametres !== null">
                    <p
                        v-if="getParametres.length === 0"
                        class="text-surface-600"
                    >
                        {{ t("alarmesV2.catalogue.carte.rienARegler") }}
                    </p>
                    <p
                        v-for="(p, i) in getParametres"
                        :key="i"
                        data-test="carte-parametre"
                    >
                        <strong class="block">{{ p.libelle }}</strong>
                        {{ p.texte }}
                    </p>
                </template>
                <p v-if="getCanaux">{{ getCanaux }}</p>
                <p v-if="getQuand">
                    <strong class="block">{{
                        t("alarmesV2.catalogue.carte.quand")
                    }}</strong>
                    {{ getQuand }}
                </p>
            </div>
        </details>
    </article>
</template>
